import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { ConfirmModal } from './ConfirmModal';

/**
 * App-native replacement for `window.confirm`.
 *
 * WHY THIS EXISTS
 *
 * ForgeDash ships as an installable PWA. A native `confirm()` breaks that
 * illusion completely — on Android it renders Chrome's own dialog, titled with
 * the origin ("app.forgedash.uk says…"), in the system font, ignoring the
 * theme. It also BLOCKS the main thread, which on a mid-range phone shows as
 * the app freezing for a beat before the dialog paints.
 *
 * The awkward part of replacing it is that `confirm()` is synchronous and a
 * modal is not, so a naive swap means hoisting `isOpen` + `pendingAction` state
 * into every component that deletes something. This provider keeps the call
 * site a one-liner by handing back a promise:
 *
 *     const confirm = useConfirm();
 *     if (!(await confirm({ title: 'Delete quote?', message: '…' }))) return;
 *
 * which is the same shape as the code it replaces — an early return on "no".
 */

export interface ConfirmOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    // The promise's resolver, held across renders. A ref rather than state
    // because resolving must not itself schedule a render.
    const resolverRef = useRef<((value: boolean) => void) | null>(null);

    const confirm = useCallback<ConfirmFn>((opts) => {
        // A second request while one is open would orphan the first promise and
        // hang whatever awaited it. Decline the outstanding one first.
        resolverRef.current?.(false);
        setOptions(opts);
        return new Promise<boolean>((resolve) => {
            resolverRef.current = resolve;
        });
    }, []);

    const settle = useCallback((value: boolean) => {
        const resolve = resolverRef.current;
        resolverRef.current = null;
        setOptions(null);
        resolve?.(value);
    }, []);

    const value = useMemo(() => confirm, [confirm]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            {options && (
                <ConfirmModal
                    isOpen
                    title={options.title}
                    message={options.message}
                    confirmText={options.confirmText}
                    cancelText={options.cancelText}
                    isDangerous={options.isDangerous}
                    onConfirm={() => settle(true)}
                    onClose={() => settle(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
}

/**
 * Returns a promise-returning confirm. Outside a ConfirmProvider it falls back
 * to `window.confirm` rather than throwing: a missing provider should degrade
 * to the old behaviour, never break a delete button.
 */
export function useConfirm(): ConfirmFn {
    const ctx = useContext(ConfirmContext);
    return useMemo<ConfirmFn>(
        () => ctx ?? (async (opts) => window.confirm(`${opts.title}\n\n${opts.message}`)),
        [ctx]
    );
}
